import React, { useState } from "react"
import { Modal, Typography } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus } from "@fortawesome/free-solid-svg-icons"

import DataQButton from "shared/ui/Buttons/DataQButton.component"
import CreateReportForm from "./CreateReportForm.component"

import { notify } from "shared/ui/Notifications/notify"
import handleSequenceError from "utils/handleSequenceError"
import { Project } from "stores/projectStore"
import { AxiosResponse } from "axios"

// Props interface for the component
interface AddReportModalProps {
  /** The project object containing project details */
  project: Project
  /** Callback function when a new report config is saved */
  onNewReportConfigSaved?: (reportName: string) => Promise<void>
}

/**
 * AddReportModal component - Displays a modal for creating a new report.
 *
 * @component
 */
const AddReportModal: React.FC<AddReportModalProps> = ({
  project,
  onNewReportConfigSaved,
}) => {
  const { Title } = Typography
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)

  /**
   * Handles actions after successfully creating a report.
   * It closes the modal and optionally triggers additional actions such as refreshing the report list.
   *
   * @function createReportCallback
   * @param res - The response object from the report creation request
   * @param reportName - The name of the created report
   */
  const createReportCallback = (
    res: AxiosResponse<unknown>,
    reportName: string
  ): void => {
    if (handleSequenceError(res, "Error creating Report!")) {
      setIsModalVisible(false)
      notify.success(
        "Report Created Successfully!",
        "The report has been created successfully."
      )
      // Call the specific handler passed from IntegratedReportSelector
      if (onNewReportConfigSaved && reportName) {
        onNewReportConfigSaved(reportName)
      }
    }
  }

  /**
   * Shows the modal by setting the modal visibility state to true.
   */
  const showModal = (): void => {
    setIsModalVisible(true)
  }

  return (
    <>
      <DataQButton onClick={showModal} type="primary" size="small">
        <FontAwesomeIcon className="mr-1" icon={faPlus} /> Create
      </DataQButton>
      <Modal
        open={isModalVisible}
        centered={true}
        footer={false}
        closable={false}
        destroyOnHidden={true}
      >
        <Title style={{ marginTop: "0.5em" }} level={4}>
          Create Dashboard
        </Title>
        <CreateReportForm
          project={project}
          setIsModalVisible={setIsModalVisible}
          createReportCallback={createReportCallback}
        />
      </Modal>
    </>
  )
}

export default AddReportModal
